import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import { Link as RouterLink } from "react-router-dom";

import MuscleHeadTextIcon from "../../assets/MuscleHeadTextIcon";

import LanguageSelect from "../shared-theme/components/LanguageSelect";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useResetPasswordMutation } from "@mutation/authMutation";
import { getOurSupportEmail } from "@config/config"
import { AlertContext } from "@context/AlertProvider";


const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(350, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(350, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  position: "relative",
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(340, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(340, 100%, 16%, 0.5), hsla(350, 30%, 5%))",
    }),
  },
}));

export default function ResetPassword(props) {

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState("");

  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { addAlertBanner } = React.useContext(AlertContext);
  const resetPasswordMutation = useResetPasswordMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateInputs())
      return;

    const data = new FormData(event.currentTarget);
    const email = data.get("email");

    resetPasswordMutation.mutate(
      {
        data: {
          email: email,
        }
      }, {
      onSuccess: () => {
        addAlertBanner(t("resetPasswordCodeSentTo") + ` ${email}`, "success")
        navigate("/verify-reset");
      },
      onError: (error) => {
        if (error.status == 404)
          addAlertBanner(t("accountNotFound"), "error")
        else if (error.status == 429)
          addAlertBanner(t("resetPassTooManyRequests"), "error")
        else
          addAlertBanner(t("error"), "error")
      }
    }

    );
  };

  const validateInputs = () => {
    const email = document.getElementById("email");

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage(t("validationErrorEmail"));
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">

        <Box
          sx={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            display: "flex",
            gap: "0.5rem",
          }}
        >
          <LanguageSelect />
        </Box>
        <Card variant="outlined">
          <MuscleHeadTextIcon
            sx={{
              alignSelf: "center",
              height: "160px",
              width: "100%",
              marginBottom: "40px",
            }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            {t("resetPassword")}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={emailError ? "error" : "primary"}
                sx={{ ariaLabel: "email" }}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={resetPasswordMutation.isPending ? true : false}
              // onClick={validateInputs}
              sx={{ marginTop: 1 }}
            >
              {t("reset", { context: "button" })}
            </Button>
          </Box>

          <Link
            component={RouterLink} to="/signin"
            type="button"
            variant="body2"

            sx={{ alignSelf: "center", marginTop: 5 }}
          >
              {t("backToLogin", { context: "button" })}
          </Link>
          <Link
            component="button"
            type="button"
            variant="body2"
            href={"mailto:" + getOurSupportEmail()}
            sx={{ alignSelf: "center", color: "gray", }}
          >
            {t("contactUs")}
          </Link>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}
